import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { TAGLINE } from '../constants/brand';

// Errors that indicate a license problem (show message directly, no "could not connect" wrapper)
const LICENSE_ERRORS = ['Desktop license not assigned', 'License not granted', 'desktop_enabled', 'Desktop license expired', 'license expired'];

const isElectron = typeof window !== 'undefined' && !!window.electronAPI;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [centralUrl, setCentralUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [bypassDone, setBypassDone] = useState(false);
  const [syncPrompt, setSyncPrompt] = useState(false);
  const { login, setLicenseExpired } = useAuth();
  const navigate = useNavigate();

  // Load persisted central URL on mount
  React.useEffect(() => {
    if (!isElectron) return;
    window.electronAPI.getCentralUrl().then(url => { if (url) setCentralUrl(url); });
  }, []);

  const handleBypassLicense = async () => {
    try {
      await window.electronAPI.bypassLicense();
      setBypassDone(true);
    } catch {
      setBypassDone(true); // still proceed
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // In Electron, the central server URL is mandatory — no offline-only mode.
    if (isElectron && !centralUrl.trim()) {
      setError('Please enter the TournNext central server URL to continue.');
      setLoading(false);
      return;
    }

    try {
      // In Electron with a central URL: authenticate against central server FIRST.
      // This verifies the license AND provisions the user in the local DB so that
      // the subsequent local login() call finds the account (critical on first install).
      let loginExpired = false;
      if (isElectron && centralUrl.trim()) {
        try {
          const result = await window.electronAPI.connectToCentralServer({
            url: centralUrl.trim().replace(/\/$/, ''),
            email,
            password,
          });
          // License expired but user was provisioned — allow login in restricted mode
          if (result?.licenseExpired) loginExpired = true;
        } catch (licErr) {
          const rawMsg = licErr.message || '';
          const msg = rawMsg.replace(/^Error invoking remote method '[^']+': Error:\s*/i, '');
          if (LICENSE_ERRORS.some(s => msg.includes(s))) {
            setError('You do not have a TournNext Desktop license. Contact your administrator.');
          } else if (/invalid.cred|unauthorized|401|invalid.user|not found/i.test(msg)) {
            setError('Invalid email or password. Please check your credentials and try again.');
          } else {
            setError('Could not connect to the central server. Please check the server URL and your internet connection.');
          }
          return;
        }
      }

      // Local login — works because central server provisioned the user above
      const loggedInUser = await login(email, password);

      if (loginExpired) {
        // Enter restricted mode — take user straight to profile to renew
        setLicenseExpired(true);
        navigate('/profile');
        return;
      }

      if (isElectron && centralUrl.trim()) {
        setSyncPrompt(true);
        return; // navigate after user responds to sync prompt
      }

      navigate(loggedInUser?.role === 'player' ? '/player/dashboard' : '/tournaments');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: 'calc(100vh - 56px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1rem',
        background: 'linear-gradient(160deg, #f0fdfa 0%, #CCFBF1 50%, #f8fafc 100%)',
      }}
    >
      <div
        style={{
          width: '100%', maxWidth: 400,
          background: 'white', borderRadius: 16, padding: '2.5rem',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0',
        }}
      >
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div
            style={{
              width: 44, height: 44, borderRadius: 12,
              background: 'linear-gradient(135deg, #0D9488 0%, #0F766E 100%)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.25rem', color: 'white', fontWeight: 700, marginBottom: '0.75rem',
            }}
          >
            T
          </div>
          <img src="/TournNextTeal-WithoutTagline.svg" alt="TournNext" style={{ height: 32, width: 'auto', display: 'block', margin: '0 auto 0.5rem' }} />
          <p style={{ fontSize: '0.72rem', color: '#9ca3af', fontStyle: 'italic', margin: '0 auto 1.25rem', textAlign: 'center' }}>{TAGLINE}</p>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: 0 }}>
            Welcome back
          </h2>
          <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: 4 }}>
            Sign in to your account
          </p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {/* Sync prompt modal — shown after successful central server connection */}
        {syncPrompt && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}>
            <div style={{
              background: 'white', borderRadius: 16, padding: '2rem',
              maxWidth: 380, width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
            }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🔄</div>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: 700 }}>
                Connected to Central Server
              </h3>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 1.5rem' }}>
                License activated. Would you like to sync your tournaments now?
              </p>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                  onClick={async () => {
                    setSyncPrompt(false);
                    navigate('/tournaments');
                    try { await window.electronAPI.triggerSync(); } catch { /* best-effort */ }
                  }}
                >
                  Sync Now
                </button>
                <button
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                  onClick={() => { setSyncPrompt(false); navigate('/tournaments'); }}
                >
                  Later
                </button>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Central Server URL — only shown in the desktop Electron app */}
          {isElectron && (
            <div className="form-group" style={{ marginBottom: '1.25rem' }}>
              <label className="form-label" style={{ color: '#0D9488', fontWeight: 600 }}>
                Central Server URL
              </label>
              <input
                type="url"
                className="form-input"
                value={centralUrl}
                onChange={(e) => setCentralUrl(e.target.value)}
                placeholder="https://api.demo.tournnext.com"
                style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
              />
              <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 4 }}>
                Leave blank to work offline only
              </p>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%', padding: '10px', fontSize: '0.95rem', marginTop: '0.5rem' }}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '0.75rem', fontSize: '0.875rem' }}>
          <Link to="/forgot-password" style={{ color: '#6b7280', textDecoration: 'none' }}>
            Forgot password?
          </Link>
        </p>

        {isElectron && (
          <div style={{ marginTop: '1.5rem', borderTop: '1px dashed #e5e7eb', paddingTop: '1rem' }}>
            {bypassDone ? (
              <div style={{
                textAlign: 'center', fontSize: '0.8rem', color: '#16a34a',
                background: '#f0fdf4', border: '1px solid #bbf7d0',
                borderRadius: 8, padding: '0.5rem 0.75rem',
              }}>
                License bypassed — log in with any account above
              </div>
            ) : (
              <button
                type="button"
                onClick={handleBypassLicense}
                style={{
                  width: '100%', padding: '8px',
                  fontSize: '0.8rem', color: '#6b7280',
                  background: 'transparent', border: '1px dashed #d1d5db',
                  borderRadius: 8, cursor: 'pointer',
                }}
              >
                Dev mode: skip license check
              </button>
            )}
          </div>
        )}

        {isElectron ? (
          <p style={{ textAlign: 'center', marginTop: '0.5rem', color: '#9ca3af', fontSize: '0.8rem' }}>
            Don&apos;t have an account?{' '}
            <span
              style={{ color: '#0D9488', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => window.electronAPI.openExternal('https://demo.tournnext.twentytwo80.com/register')}
            >
              Register on the web
            </span>
          </p>
        ) : (
          <p style={{ textAlign: 'center', marginTop: '0.5rem', color: '#6b7280', fontSize: '0.875rem' }}>
            Don&apos;t have an account?{' '}
            <Link to="/register" style={{ color: '#0D9488', fontWeight: 500, textDecoration: 'none' }}>
              Create one
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default Login;
